import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { http, apiError, downloadCSV, mediaUrl } from "@/lib/api";
import { LoginScreen, KPI, StatusBadge, Modal, EmptyState, Logo } from "@/components/shared";
import { CreativePreview } from "@/portals/OpsPortal";
import { Shell, PageHead, Card } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  LayoutDashboard, Film, Upload as UploadIcon, Send, BarChart3,
  UserCog, LifeBuoy, Loader2, PlayCircle, Trash2, Minus, Plus, Radio, Download,
  Check, CheckCircle2, Mail, Phone, MessageCircle,
} from "lucide-react";

const ACCENT = "#35A787";
const SCREENS = ["overview", "creatives", "upload", "request", "reports", "account", "support"];
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "creatives", label: "Creatives", icon: Film },
  { key: "upload", label: "Upload ad", icon: UploadIcon },
  { key: "request", label: "Request cabs", icon: Send },
  { key: "reports", label: "Reports", icon: BarChart3 },
  { key: "account", label: "Account", icon: UserCog },
  { key: "support", label: "Support", icon: LifeBuoy },
];

// light-theme helpers
const labelCls = "mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-stone-500";
const H3 = ({ children }) => <h3 className="mb-3 font-display text-base font-bold tracking-tight text-stone-900">{children}</h3>;
const Row = ({ l, v, bold, icon: Icon }) => (
  <div className="flex items-center justify-between py-3 text-sm">
    <span className="flex items-center gap-2 text-stone-500">{Icon && <Icon size={14} className="text-stone-400" />}{l}</span>
    <span className={`font-mono ${bold ? "font-bold text-stone-900" : "text-stone-700"}`}>{v}</span>
  </div>
);

export default function BusinessPortal() {
  const { user } = useAuth();
  const { screen } = useParams();
  const navigate = useNavigate();
  const go = (s) => navigate(`/business/${s}`);
  const active = SCREENS.includes(screen) ? screen : "overview";
  if (user === null) return <FullLoader />;
  if (!user || user.portal !== "business")
    return <LoginScreen portal="business" demoCreds={[
      { id: "owner@pizzapalace.com", pw: "demo1234", label: "Pizza Palace · 15 cabs" },
      { id: "ops@kaveri.in", pw: "demo1234", label: "Kaveri · 25 cabs" },
    ]} />;
  return (
    <Shell accent={ACCENT} portalName="Advertiser Portal" nav={NAV} active={active} onNav={go} theme="light">
      {active === "overview" && <Overview go={go} />}
      {active === "creatives" && <Creatives go={go} />}
      {active === "upload" && <UploadAd go={go} />}
      {active === "request" && <RequestCabs />}
      {active === "reports" && <Reports />}
      {active === "account" && <Account />}
      {active === "support" && <Support />}
    </Shell>
  );
}

const FullLoader = () => <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-stone-400" /></div>;
function useFetch(url, dep = [], every = 0) {
  const [data, setData] = useState(null);
  const reload = useCallback(() => { http.get(url).then((r) => setData(r.data)).catch(() => {}); }, [url]);
  useEffect(() => {
    reload();
    const t = every > 0 ? setInterval(reload, every) : null;
    return () => { if (t) clearInterval(t); };
    /* eslint-disable-next-line */
  }, [url, every, ...dep]);
  return [data, reload, setData];
}

const AnimatedPreview = ({ creative }) => (
  <div className="relative aspect-video overflow-hidden rounded-xl border-4 border-stone-900 bg-black">
    {creative?.video ? (
      <video src={mediaUrl(creative.video)} autoPlay muted loop playsInline className="h-full w-full object-contain" />
    ) : (
      <div className="flex h-full w-full flex-col items-center justify-center bg-stone-900 px-6 text-center">
        <Logo size={30} variant="white" />
        <div className="mt-3 font-display text-lg font-bold text-white">{creative?.title || "Your ad"}</div>
        <div className="mt-1 text-xs text-stone-400">Headrest ad · {creative?.duration || 15}s · plays in a 4-brand rotation</div>
      </div>
    )}
    <div className="absolute bottom-0 left-0 h-1 bg-white/80" style={{ animation: `progressBar ${creative?.duration || 15}s linear infinite` }} />
  </div>
);

// ---------------- Overview ----------------
function Overview({ go }) {
  const [o] = useFetch("/business/overview", [], 10000);
  const [requests] = useFetch("/cab-requests/me", [], 10000);
  const [plays, setPlays] = useState(0);
  useEffect(() => {
    if (!o) return;
    setPlays(Math.floor(o.plays_today * 0.42));
    const perSec = o.plays_today / (12 * 3600);
    const t = setInterval(() => setPlays((p) => p + Math.max(1, Math.round(perSec * 2))), 2000);
    return () => clearInterval(t);
  }, [o]);
  if (!o) return <FullLoader />;

  const hasAds = (o.pending || 0) + (o.approved || 0) > 0;
  const hasOnAir = !!o.on_air;
  const hasCabs = (o.cabs || 0) > 0;
  const fullyLive = hasOnAir && hasCabs;

  // Latest cab request state for the guided panel.
  const latestReq = requests && requests.length ? requests[0] : null;

  const steps = [
    { label: "Account created", state: "done", note: "You're signed in and ready", action: null },
    { label: "Upload an ad", state: hasAds ? "done" : "next", note: hasAds ? "Your ad is saved" : "Add your first 15s video",
      action: hasAds ? null : { label: "Upload ad", onClick: () => go("upload") } },
    { label: "Put an ad on air", state: hasOnAir ? "done" : (hasAds ? "next" : "wait"),
      note: hasOnAir ? "Your ad is playing" : hasAds ? "Pick which ad plays on the road" : "Upload an ad first",
      action: hasAds && !hasOnAir ? { label: "Go to creatives", onClick: () => go("creatives") } : null },
    { label: "Get cabs", state: hasCabs ? "done" : (requests && latestReq ? "inprogress" : "next"),
      note: hasCabs ? `${o.cabs} cabs on the road` : latestReq ? (latestReq.status === "approved" ? `${latestReq.assigned || 0} cabs assigned` : (latestReq.status === "pending" ? "We're assigning your cabs" : `Request rejected — ${latestReq.reject_reason || "no reason given"}`)) : "Request screens for your ad",
      action: !hasCabs && (!latestReq || latestReq.status !== "pending") ? { label: "Request cabs", onClick: () => go("request") } : null },
  ];

  return (
    <>
      <PageHead title="Campaign Overview" sub={`${o.cabs} cabs on the road across Pune · updates live`} />

      {!fullyLive && (
        <Card className="mb-6 p-5">
          <div className="mb-3 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#35A787]/10 px-2.5 py-1 text-[11px] font-semibold text-[#0B6E4F]">
              <CheckCircle2 size={13} /> Get on the road
            </span>
            <span className="text-xs font-medium text-stone-500">Your account is active — here's how to go live</span>
          </div>
          <ol className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => {
              const color = s.state === "done" ? "bg-emerald-500 text-white" : s.state === "next" ? "bg-[#35A787] text-white" : "bg-stone-200 text-stone-400";
              const icon = s.state === "done" ? <Check size={14} /> : <span>{i + 1}</span>;
              return (
                <li key={s.label} className={`flex flex-col gap-1.5 rounded-xl border p-3 ${s.state === "done" ? "border-emerald-200 bg-emerald-50/60" : "border-[#EAE9E4] bg-white"}`}>
                  <div className="flex items-center gap-2">
                    <span className={`flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-bold ${color}`}>{icon}</span>
                    <span className="text-sm font-semibold text-stone-800">{s.label}</span>
                  </div>
                  <p className="text-xs text-stone-500">{s.note}</p>
                  {s.action && <Button size="sm" className="mt-1 w-fit flex-none bg-[#35A787] text-white hover:bg-[#2D8F74]" onClick={s.action.onClick}>{s.action.label}</Button>}
                </li>
              );
            })}
          </ol>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <KPI label="Cabs on the road" value={o.cabs} sub={`${o.slots} ad slots`} accent={ACCENT} />
        <KPI label="Distance today" value={`${o.km_today.toLocaleString("en-IN")} km`} accent={ACCENT} />
        <KPI label="Est. reach" value={o.reach_estimate.toLocaleString("en-IN")} sub="passengers/day · est. 3 riders per play" accent={ACCENT} />
        <KPI label="Ready ads" value={o.approved} accent={ACCENT} />
        <KPI label="Uptime" value={`${o.uptime}%`} accent={ACCENT} />
      </div>
      <Card className="mt-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm text-stone-500">{o.on_air ? <span className="flex items-center gap-1.5"><span className="live-dot h-2 w-2 rounded-full bg-emerald-500" /> On air now</span> : <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> Nothing on air yet</span>}
          </div>
          <span className="text-xs font-medium text-stone-600">
            {o.on_air ? o.on_air.title
              : hasAds ? "Pick one of your ads to start playing"
              : <button onClick={() => go("upload")} className="font-semibold text-[#2D8F74] hover:underline">Upload your first ad to get started →</button>}
          </span>
        </div>
        <AnimatedPreview creative={o.on_air} />
        <div className="mt-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-stone-400">Live plays today</div>
            <div className="mt-1 flex items-center gap-2"><div data-testid="business-overview-play-counter" className="font-display text-5xl font-extrabold tabular-nums" style={{ color: ACCENT }}>{plays.toLocaleString("en-IN")}</div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${o.live ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{o.live ? "● Live device data" : "◌ Simulated baseline"}</span></div>
          </div>
          <div className="text-xs text-stone-400">Projected <span className="font-mono font-semibold text-stone-600">{o.plays_today.toLocaleString("en-IN")}</span> by end of day</div>
        </div>
      </Card>
    </>
  );
}

// ---------------- Creatives ----------------
function Creatives({ go }) {
  const [creatives, reload] = useFetch("/creatives", [], 8000);
  const [preview, setPreview] = useState(null);
  const [delFor, setDelFor] = useState(null);
  if (!creatives) return <FullLoader />;
  const onAir = async (c) => { try { await http.patch(`/creatives/${c.id}/on-air`); toast.success("On air!"); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const del = async () => { try { await http.delete(`/creatives/${delFor.id}`); toast.success("Deleted"); setDelFor(null); reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); setDelFor(null); } };
  return (
    <>
      <PageHead title="Creatives" sub="Your ad library"
        action={<Button data-testid="business-creatives-upload-btn" onClick={() => go("upload")}><Plus size={16} /> Upload ad</Button>} />
      {creatives.length === 0 && (
        <Card className="p-0">
          <EmptyState icon={Film} title="No creatives yet" sub="Upload your first ad to get it on the road."
            action={<Button onClick={() => go("upload")}><Plus size={16} /> Upload ad</Button>} />
        </Card>
      )}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {creatives.map((c) => (
          <Card key={c.id}>
            <div className="relative mb-3 flex aspect-video items-center justify-center overflow-hidden rounded-lg border border-stone-200 bg-stone-50">
              <button onClick={() => setPreview(c)} className="flex flex-col items-center gap-1 text-stone-400 transition-colors hover:text-stone-600">
                <PlayCircle size={36} /><span className="text-xs font-medium">{c.duration}s</span>
              </button>
              {c.on_air && <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-white"><Radio size={10} /> ON AIR</span>}
            </div>
            <div className="flex items-start justify-between gap-2"><div className="font-semibold text-stone-900">{c.title}</div><StatusBadge status={c.status} /></div>
            {c.status === "rejected" && c.reject_reason && <div className="mt-2 rounded-md bg-rose-50 px-2 py-1.5 text-xs text-rose-600">Reason: {c.reject_reason}</div>}
            <div className="mt-3 flex gap-2">
              {c.status === "approved" && !c.on_air && <Button onClick={() => onAir(c)} className="flex-1"><PlayCircle size={14} /> Put on air</Button>}
              <Button variant="outline" onClick={() => setPreview(c)} className="flex-1">Preview</Button>
              <Button variant="outline" size="icon" onClick={() => setDelFor(c)} className="text-stone-400 hover:text-rose-500"><Trash2 size={14} /></Button>
            </div>
          </Card>
        ))}
      </div>
      <CreativePreview creative={preview} onClose={() => setPreview(null)} />
      <Modal open={!!delFor} onClose={() => setDelFor(null)} title="Delete creative?">
        <p className="text-sm text-stone-500">This can't be undone. Creatives currently on air can't be deleted.</p>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={() => setDelFor(null)} className="flex-1">Cancel</Button>
          <Button variant="destructive" onClick={del} className="flex-1">Delete</Button>
        </div>
      </Modal>
    </>
  );
}

// ---------------- Upload ----------------
function UploadAd({ go }) {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [duration, setDuration] = useState(0);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => setDuration(Math.round(v.duration * 10) / 10);
    v.src = URL.createObjectURL(f);
  };
  const submit = async () => {
    if (!title.trim() || !file) return toast.error("Add a title and pick a video");
    if (duration > 15.9) { toast.error("Ad video is longer than 15 seconds — please cut it to 15s or less."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("duration", String(duration || 15));
      fd.append("file", file);
      await http.post("/creatives", fd);
      toast.success("Ad saved — ready to go on air"); go("creatives");
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <>
      <PageHead title="Upload a new ad" sub="MP4 recommended · 15s plays in a 4-brand rotation" />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Ad title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Weekend Combo Offer" />
            </div>
            <div>
              <label className={labelCls}>Video file</label>
              <input ref={fileRef} type="file" accept="video/*" onChange={onFile} className="hidden" />
              <Button type="button" variant="outline" onClick={() => fileRef.current.click()} className="h-auto w-full flex-col gap-2 py-8 text-stone-500">
                <UploadIcon size={18} />
                <span className="text-sm font-medium">{file ? "Change video" : "Pick a video file"}</span>
              </Button>
              {duration > 0 && <div className={`mt-2 text-xs font-medium ${duration > 15.9 ? "text-red-600" : "text-emerald-600"}`}>Duration read: {duration}s{duration > 15.9 ? " — must be 15s or less" : ""}</div>}
            </div>
            <Button onClick={submit} disabled={busy} className="w-full">{busy && <Loader2 size={16} className="animate-spin" />} Save ad</Button>
          </div>
        </Card>
        <div className="space-y-4">
          <Card>
            <H3>Live preview</H3>
            <div className="flex aspect-video items-center justify-center overflow-hidden rounded-lg border-4 border-stone-900 bg-black">
              {previewUrl ? <video src={previewUrl} controls className="h-full w-full object-contain" /> : <div className="text-sm text-stone-400">Preview appears here</div>}
            </div>
          </Card>
          <Card>
            <H3>Content policy</H3>
            <ul className="space-y-2.5 text-sm text-stone-600">
              <li className="flex items-start gap-2.5"><Check size={15} className="mt-0.5 flex-none text-emerald-500" /> No adult, alcohol or tobacco content</li>
              <li className="flex items-start gap-2.5"><Check size={15} className="mt-0.5 flex-none text-emerald-500" /> Clear, legible, 16:9, min 720p</li>
              <li className="flex items-start gap-2.5"><Check size={15} className="mt-0.5 flex-none text-emerald-500" /> Your ad goes live the moment you put it on air</li>
              <li className="flex items-start gap-2.5"><Check size={15} className="mt-0.5 flex-none text-emerald-500" /> Must include your brand / contact</li>
            </ul>
          </Card>
        </div>
      </div>
    </>
  );
}

// ---------------- Request cabs ----------------
const STEPS = ["Set count", "Add note", "Send request"];
function RequestCabs() {
  const { config } = useAuth();
  const MIN = config?.rules?.MIN_CABS || 15;
  const [reqs, reload] = useFetch("/cab-requests/me", [], 8000);
  const [count, setCount] = useState(MIN);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setCount(MIN); }, [MIN]);
  if (!reqs) return <FullLoader />;
  const presets = [15, 25, 50, 100];
  const submit = async () => {
    if (count < MIN) return toast.error(`Minimum ${MIN} cabs per request`);
    setBusy(true);
    try {
      await http.post("/cab-requests", { count, note });
      toast.success("Request sent — our team will confirm shortly");
      setNote(""); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <PageHead title="Request more cabs" sub={`${config?.brand?.city || "Pune"} fleet · ₹${(config?.rules?.PRICE_PER_SLOT || 4300).toLocaleString("en-IN")}/slot/month · min ${MIN} cabs`} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="mb-6 flex items-center gap-1.5">
            {STEPS.map((s, i) => (
              <div key={s} className="flex min-w-0 flex-1 items-center gap-2">
                <div className="flex flex-none items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-stone-900 text-[10px] font-bold text-white">{i + 1}</span>
                  <span className="whitespace-nowrap text-[11px] font-medium uppercase tracking-wider text-stone-400">{s}</span>
                </div>
                {i < STEPS.length - 1 && <span className="mx-2 h-px min-w-4 flex-1 border-t border-dotted border-stone-300" />}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-4">
            <Button variant="outline" size="icon" onClick={() => setCount((c) => Math.max(MIN, c - 1))} className="h-11 w-11 rounded-full"><Minus size={18} /></Button>
            <Input data-testid="business-cab-request-count" type="number" value={count} min={MIN}
              onChange={(e) => setCount(Math.max(MIN, Number(e.target.value) || MIN))}
              className="h-14 w-28 text-center font-display text-2xl font-bold text-stone-900" />
            <Button variant="outline" size="icon" onClick={() => setCount((c) => c + 1)} className="h-11 w-11 rounded-full"><Plus size={18} /></Button>
          </div>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {presets.map((p) => <Button key={p} size="sm" onClick={() => setCount(p)} variant={count === p ? "default" : "outline"} className="rounded-full">{p}</Button>)}
          </div>
          <label className={labelCls + " mt-5"}>Anything we should know? (campaign, zones, timing)</label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="business-cab-request-note"
            placeholder="e.g. Need these on-screen before Diwali" />
          <Button onClick={submit} disabled={busy} data-testid="business-cab-request-submit"
            className="mt-4 w-full">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send request
          </Button>
        </Card>
        <Card>
          <div className="mb-3 flex items-center justify-between">
            <H3>My requests</H3>
            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> Live — updates automatically</span>
          </div>
          {reqs.length === 0 ? (
            <EmptyState icon={Send} title="No requests yet" sub="Your cab requests will appear here once you send one." />
          ) : (
            <div className="space-y-3" data-testid="business-cab-request-list">
              {reqs.map((r) => (
                <div key={r.id} className="rounded-lg border border-stone-200 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-stone-900">{r.count} cabs</div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="mt-0.5 text-xs text-stone-500">{new Date(r.created_at).toLocaleString("en-IN")}</div>
                  {r.note && <div className="mt-1 text-sm text-stone-600">“{r.note}”</div>}
                  {r.status === "approved" && <div className="mt-1 flex items-center gap-1 text-xs font-medium text-emerald-600"><CheckCircle2 size={13} /> {r.assigned} cab{String(r.assigned) === "1" ? "" : "s"} assigned{r.waitlist ? ` · ${r.waitlist} waitlisted` : ""}</div>}
                  {r.status === "rejected" && r.reject_reason && <div className="mt-1 text-xs font-medium text-rose-500">Reason: {r.reject_reason}</div>}
                  {r.status === "pending" && <div className="mt-1 text-xs text-amber-600">We'll confirm shortly.</div>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

// ---------------- Reports ----------------
function Reports() {
  const [r] = useFetch("/reports/plays");
  if (!r) return <FullLoader />;
  const exportCSV = () => {
    const rows = [["Plate", "Plays"], ...r.per_cab.map((c) => [c.plate, c.plays])];
    downloadCSV("anand-report.csv", rows); toast.success("CSV downloaded");
  };
  return (
    <>
      <PageHead title="Reports" sub="Your ad plays — daily trends & per-cab performance"
        action={<Button variant="outline" onClick={exportCSV}><Download size={16} /> Export CSV</Button>} />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <H3>Plays per day</H3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={r.daily}><defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={ACCENT} stopOpacity={0.6} /><stop offset="100%" stopColor={ACCENT} stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#e7e1d8" vertical={false} /><XAxis dataKey="date" tick={{ fill: "#a8a29e", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} /><YAxis tick={{ fill: "#a8a29e", fontSize: 10 }} /><Tooltip contentStyle={{ background: "#fff", border: "1px solid #e7e1d8", borderRadius: 8 }} />
              <Area type="monotone" dataKey="plays" stroke={ACCENT} fill="url(#g)" strokeWidth={2} /></AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <H3>Plays by hour</H3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={r.hourly}><CartesianGrid stroke="#e7e1d8" vertical={false} /><XAxis dataKey="hour" tick={{ fill: "#a8a29e", fontSize: 10 }} /><YAxis tick={{ fill: "#a8a29e", fontSize: 10 }} /><Tooltip contentStyle={{ background: "#fff", border: "1px solid #e7e1d8", borderRadius: 8 }} /><Bar dataKey="plays" fill={ACCENT} radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
      <div className="chart-panel mt-6">
        <div className="panel-head">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6B6659]">Per-cab plays</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.live ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{r.live ? "● Live device data" : "◌ Simulated baseline"}</span>
        </div>
        <div className="panel-body overflow-x-auto">
          <table className="ent-table">
            <thead>
              <tr>{["Cab", "Plays"].map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {r.per_cab.map((c) => <tr key={c.plate}><td className="num text-[#374151]">{c.plate}</td><td className="num">{c.plays}</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ---------------- Account ----------------
function Account() {
  const [adv] = useFetch("/advertisers/me");
  const { user } = useAuth();
  if (!adv) return <FullLoader />;
  return (
    <>
      <PageHead title="Account" sub="Business details & preferences" />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <H3>Business</H3>
          <div className="divide-y divide-stone-100">
            <Row l="Name" v={adv.name} />
            <Row l="Email" v={adv.email} />
            <Row l="Category" v={adv.category} />
            <Row l="GSTIN" v={adv.gstin || "—"} />
            <Row l="Renewal" v={adv.renewal_date} />
          </div>
        </Card>
        <Card>
          <H3>Notifications & team</H3>
          <div className="divide-y divide-stone-100">
            <Row l="Email alerts" v={adv.notify_email ? "On" : "Off"} />
            <Row l="SMS alerts" v={adv.notify_sms ? "On" : "Off"} />
          </div>
          <div className="mt-3 border-t border-stone-200 pt-3"><Row l="Signed in as" v={user?.email} /></div>
        </Card>
      </div>
    </>
  );
}

// ---------------- Support ----------------
function Support() {
  const { config } = useAuth();
  const b = config?.brand || {};
  const faqs = [
    ["What is the minimum booking?", `${config?.rules?.MIN_CABS || 15} cabs per request.`],
    ["How do I get more cabs?", "Use 'Request cabs' — our team reviews and assigns available cabs to you, usually within a day."],
    ["When does my ad go live?", "After admin approval (within 2 working hours), tap 'Put on air'."],
  ];
  return (
    <>
      <PageHead title="Support" sub="We're here to help" />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <H3>Contact</H3>
          <div className="divide-y divide-stone-100">
            <Row icon={Mail} l="Email" v={b.support_email} />
            <Row icon={Phone} l="Phone" v={b.phone} />
            <Row icon={MessageCircle} l="WhatsApp" v={b.whatsapp} />
          </div>
        </Card>
        <Card>
          <H3>FAQ</H3>
          <div className="space-y-4">{faqs.map(([q, a]) => <div key={q} className="border-b border-stone-100 pb-4 last:border-0 last:pb-0"><div className="text-sm font-semibold text-stone-900">{q}</div><div className="mt-0.5 text-sm text-stone-500">{a}</div></div>)}</div>
        </Card>
      </div>
    </>
  );
}